import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { listServices, exportServicesToExcel } from "../../api/api";
import {
  ResponsiveContainer,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  BarChart,
  Bar,
  Legend,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
} from "recharts";
import styles from "./AnalyticsDashboard.module.css";

const COLORS = ["#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6"];

const AnalyticsDashboard = () => {
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exportLoading, setExportLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [userRole, setUserRole] = useState("");
  const [filters, setFilters] = useState({
    startDate: new Date(new Date().setDate(new Date().getDate() - 30))
      .toISOString()
      .split("T")[0],
    endDate: new Date().toISOString().split("T")[0],
    location: "all",
  });

  useEffect(() => {
    const role = localStorage.getItem("adminRole");
    setUserRole(role || "");
    if (role === "superadmin") {
      fetchServices();
    } else {
      navigate("/stringer-dashboard");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (userRole === "superadmin") {
      fetchServices();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.startDate, filters.endDate, filters.location]);

  const fetchServices = async () => {
    try {
      setLoading(true);
      const params = { 
        limit: 1000,
        start_date: filters.startDate,
        end_date: filters.endDate
      };
      if (filters.location !== "all") {
        params.location = filters.location;
      }
      const response = await listServices(params);
      setServices(response.data);
    } catch (error) {
      console.error("Error fetching services:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      setExportLoading(true);

      const params = {
        startDate: filters.startDate,
        endDate: filters.endDate,
      };
      if (filters.location !== "all") {
        params.location = filters.location;
      }

      const response = await exportServicesToExcel(params);

      const blob = new Blob([response], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      const locationLabel =
        filters.location !== "all"
          ? `_${filters.location.replace(/\s+/g, "_")}`
          : "";
      const filename = `services${locationLabel}_${filters.startDate}_to_${
        filters.endDate
      }_${new Date().toISOString().split("T")[0]}.xlsx`;
      link.download = filename;

      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setSuccessMessage("Services exported successfully!");
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error) {
      console.error("Error exporting services:", error);
      alert(error.response?.data?.message || "Failed to export services");
    } finally {
      setExportLoading(false);
    }
  };

  // Services are already filtered by the backend based on date range
  const filteredServices = services;

  // Analytics calculations
  const servicesPerDay = useMemo(() => {
    const dayMap = {};
    filteredServices.forEach((service) => {
      const date = service.service_date?.split("T")[0] || "Unknown";
      const revenue = parseFloat(
        service.total_payment || service.cost_per_unit || 0
      );
      if (!dayMap[date]) {
        dayMap[date] = { date, count: 0, revenue: 0 };
      }
      dayMap[date].count += 1;
      dayMap[date].revenue += revenue;
    });
    return Object.values(dayMap).sort((a, b) => a.date.localeCompare(b.date));
  }, [filteredServices]);

  const servicesByLocation = useMemo(() => {
    const locationMap = {};
    filteredServices.forEach((service) => {
      // Determine where the racket is from
      let location;
      if (service.racket_from_marina === "yes") {
        location = "Marina Store";
      } else {
        location =
          service.location || service.serviced_at_location || "Unknown";
      }

      const revenue = parseFloat(
        service.total_payment || service.cost_per_unit || 0
      );
      if (!locationMap[location]) {
        locationMap[location] = { location, count: 0, revenue: 0 };
      }
      locationMap[location].count += 1;
      locationMap[location].revenue += revenue;
    });
    return Object.values(locationMap);
  }, [filteredServices]);

  const servicesByType = useMemo(() => {
    const typeMap = {};
    filteredServices.forEach((service) => {
      const type = service.service_type || "Unknown";
      const revenue = parseFloat(
        service.total_payment || service.cost_per_unit || 0
      );
      if (!typeMap[type]) {
        typeMap[type] = { type, count: 0, revenue: 0 };
      }
      typeMap[type].count += 1;
      typeMap[type].revenue += revenue;
    });
    return Object.values(typeMap);
  }, [filteredServices]);

  const servicesByStringer = useMemo(() => {
    const stringerMap = {};
    filteredServices.forEach((service) => {
      const stringer = service.performed_by || "Unknown";
      const revenue = parseFloat(
        service.total_payment || service.cost_per_unit || 0
      );
      if (!stringerMap[stringer]) {
        stringerMap[stringer] = { stringer, count: 0, revenue: 0 };
      }
      stringerMap[stringer].count += 1;
      stringerMap[stringer].revenue += revenue;
    });
    return Object.values(stringerMap)
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 10);
  }, [filteredServices]);

  const topStrings = useMemo(() => {
    const stringMap = {};
    filteredServices.forEach((service) => {
      let stringName;
      if (service.customer_string === "yes") {
        stringName = service.custom_string_name || "Customer String (Unknown)";
      } else {
        stringName = service.string_name || "Unknown";
      }
      const revenue = parseFloat(
        service.total_payment || service.cost_per_unit || 0
      );
      if (!stringMap[stringName]) {
        stringMap[stringName] = { string: stringName, count: 0, revenue: 0 };
      }
      stringMap[stringName].count += 1;
      stringMap[stringName].revenue += revenue;
    });
    return Object.values(stringMap)
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);
  }, [filteredServices]);

  const stringSourceBreakdown = useMemo(() => {
    const customerStrings = filteredServices.filter(
      (s) => s.customer_string === "yes"
    ).length;
    const inventoryStrings = filteredServices.length - customerStrings;
    return [
      {
        source: "Inventory Strings",
        count: inventoryStrings,
        revenue: filteredServices
          .filter((s) => s.customer_string !== "yes")
          .reduce(
            (sum, s) =>
              sum +
              (parseFloat(s.total_payment) || parseFloat(s.cost_per_unit) || 0),
            0
          ),
      },
      {
        source: "Customer Strings",
        count: customerStrings,
        revenue: filteredServices
          .filter((s) => s.customer_string === "yes")
          .reduce(
            (sum, s) =>
              sum +
              (parseFloat(s.total_payment) || parseFloat(s.cost_per_unit) || 0),
            0
          ),
      },
    ];
  }, [filteredServices]);

  const stats = {
    totalServices: filteredServices.length,
    totalRevenue: filteredServices.reduce(
      (sum, s) =>
        sum + (parseFloat(s.total_payment) || parseFloat(s.cost_per_unit) || 0),
      0
    ),
    avgRevenue:
      filteredServices.length > 0
        ? filteredServices.reduce(
            (sum, s) =>
              sum +
              (parseFloat(s.total_payment) || parseFloat(s.cost_per_unit) || 0),
            0
          ) / filteredServices.length
        : 0,
    onlineServices: filteredServices.filter((s) => s.service_type === "online")
      .length,
    offlineServices: filteredServices.filter(
      (s) => s.service_type === "offline"
    ).length,
    walkInServices: filteredServices.filter((s) => s.service_type === "walk-in")
      .length,
  };

  if (loading) {
    return (
      <div className={styles.loading}>
        <div className={styles.loadingText}>Loading Analytics...</div>
      </div>
    );
  }

  return (
    <div className={styles.fullScreenContainer}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <button
            onClick={() => navigate("/dashboard/stringer-dashboard")}
            className={styles.backButton}
          >
            ← Back to Services
          </button>
          <h1 className={styles.title}>Analytics Dashboard</h1>
          <button
            onClick={handleExport}
            disabled={exportLoading}
            className={styles.exportButton}
          >
            {exportLoading ? "Exporting..." : "Export to Excel"}
          </button>
        </div>

        {successMessage && (
          <div className={styles.successMessage}>{successMessage}</div>
        )}

        {/* Filters */}
        <div className={styles.filters}>
          <div className={styles.filterGroup}>
            <label>Start Date</label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) =>
                setFilters({ ...filters, startDate: e.target.value })
              }
              className={styles.filterInput}
            />
          </div>
          <div className={styles.filterGroup}>
            <label>End Date</label>
            <input
              type="date"
              value={filters.endDate}
              min={filters.startDate}
              onChange={(e) =>
                setFilters({ ...filters, endDate: e.target.value })
              }
              className={styles.filterInput}
            />
          </div>
          <div className={styles.filterGroup}>
            <label>Location</label>
            <select
              value={filters.location}
              onChange={(e) =>
                setFilters({ ...filters, location: e.target.value })
              }
              className={styles.filterInput}
            >
              <option value="all">All Locations</option>
              <option value="Jumeirah Store">Jumeirah Store</option>
              <option value="Marina Store">Marina Store</option>
              <option value="Dubai Hills Store">Dubai Hills Store</option>
            </select>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className={styles.statsRow}>
        <div className={styles.statCard}>
          <div className={styles.statLabel}>Total Services</div>
          <div className={styles.statValue}>{stats.totalServices}</div>
          <div className={styles.statChange}>
            Period: {filters.startDate} to {filters.endDate}
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statLabel}>Total Revenue</div>
          <div className={styles.statValue}>
            AED {stats.totalRevenue.toFixed(2)}
          </div>
          <div className={styles.statChange}>Across all locations</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statLabel}>Average Revenue</div>
          <div className={styles.statValue}>
            AED {stats.avgRevenue.toFixed(2)}
          </div>
          <div className={styles.statChange}>Per service</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statLabel}>Active Stringers</div>
          <div className={styles.statValue}>{servicesByStringer.length}</div>
          <div className={styles.statChange}>Contributing members</div>
        </div>
      </div>

      {/* Charts Grid */}
      <div className={styles.chartsContainer}>
        {/* Revenue Trend - Full Width */}
        <div className={styles.chartCardFull}>
          <h3 className={styles.chartTitle}>Revenue & Services Trend</h3>
          <ResponsiveContainer width="100%" height={350}>
            <AreaChart data={servicesPerDay}>
              <defs>
                <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8} />
                  <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="colorCount" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10b981" stopOpacity={0.8} />
                  <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis
                dataKey="date"
                tickFormatter={(date) => {
                  const d = new Date(date);
                  return `${d.getDate()}/${d.getMonth() + 1}`;
                }}
                stroke="#6b7280"
              />
              <YAxis yAxisId="left" stroke="#3b82f6" />
              <YAxis yAxisId="right" orientation="right" stroke="#10b981" />
              <Tooltip
                labelFormatter={(date) => new Date(date).toLocaleDateString()}
                formatter={(value, name) => {
                  if (name === "revenue")
                    return [`AED ${value.toFixed(2)}`, "Revenue"];
                  return [value, "Services"];
                }}
              />
              <Legend />
              <Area
                yAxisId="right"
                type="monotone"
                dataKey="revenue"
                stroke="#3b82f6"
                fillOpacity={1}
                fill="url(#colorRevenue)"
                name="Revenue (AED)"
              />
              <Area
                yAxisId="left"
                type="monotone"
                dataKey="count"
                stroke="#10b981"
                fillOpacity={1}
                fill="url(#colorCount)"
                name="Services"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Services by Location */}
        <div className={styles.chartCard}>
          <h3 className={styles.chartTitle}>Performance by Location</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={servicesByLocation}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="location" stroke="#6b7280" />
              <YAxis stroke="#6b7280" />
              <Tooltip />
              <Legend />
              <Bar
                dataKey="count"
                fill="#3b82f6"
                name="Services"
                radius={[8, 8, 0, 0]}
              />
              <Bar
                dataKey="revenue"
                fill="#10b981"
                name="Revenue (AED)"
                radius={[8, 8, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Service Type Distribution */}
        <div className={styles.chartCard}>
          <h3 className={styles.chartTitle}>Service Type Distribution</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={servicesByType}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ type, percent }) =>
                  `${type}: ${(percent * 100).toFixed(0)}%`
                }
                outerRadius={100}
                fill="#8884d8"
                dataKey="count"
              >
                {servicesByType.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={COLORS[index % COLORS.length]}
                  />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Top Stringers - Full Width */}
        <div className={styles.chartCardFull}>
          <h3 className={styles.chartTitle}>Top Stringers Performance</h3>
          <ResponsiveContainer width="100%" height={350}>
            <BarChart data={servicesByStringer} layout="horizontal">
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="stringer" stroke="#6b7280" />
              <YAxis stroke="#6b7280" />
              <Tooltip
                formatter={(value, name) => {
                  if (name === "revenue")
                    return [`AED ${value.toFixed(2)}`, "Revenue"];
                  return [value, "Services"];
                }}
              />
              <Legend />
              <Bar
                dataKey="count"
                fill="#8b5cf6"
                name="Services"
                radius={[8, 8, 0, 0]}
              />
              <Bar
                dataKey="revenue"
                fill="#f59e0b"
                name="Revenue (AED)"
                radius={[8, 8, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Most Popular Strings */}
        <div className={styles.chartCard}>
          <h3 className={styles.chartTitle}>Most Popular Strings</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={topStrings} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis type="number" stroke="#6b7280" />
              <YAxis
                dataKey="string"
                type="category"
                width={120}
                stroke="#6b7280"
              />
              <Tooltip />
              <Bar
                dataKey="count"
                fill="#ec4899"
                name="Services"
                radius={[0, 8, 8, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* String Source Breakdown */}
        <div className={styles.chartCard}>
          <h3 className={styles.chartTitle}>String Source Breakdown</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={stringSourceBreakdown}
                cx="50%"
                cy="50%"
                labelLine={true}
                label={({ source, count, percent }) =>
                  `${source}: ${count} (${(percent * 100).toFixed(0)}%)`
                }
                outerRadius={100}
                fill="#8884d8"
                dataKey="count"
              >
                <Cell fill="#06b6d4" />
                <Cell fill="#f97316" />
              </Pie>
              <Tooltip
                formatter={(value, name) => {
                  if (name === "count") return [value, "Services"];
                  if (name === "revenue")
                    return [`AED ${value.toFixed(2)}`, "Revenue"];
                  return [value, name];
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsDashboard;
